/**
 * Main page container for the vault site.
 * - Renders header content with slotted page content.
 * - Initializes and manages Sentry client.
 */
import { html, css, LitElement, TemplateResult } from "lit";
import { property, customElement, query } from "lit/decorators.js";
import vaultLogo from "../../images/vault-logo-3.png";
import { getCsrfHeader } from "@internetarchive/ads-library";
import { VaultSentry } from "../../lib/sentryClient";
import { desktopState } from "../../state/desktop";

export enum TopNavUrlPatterns {
  Dashboard = "dashboard",
  Collections = "collections",
  // Details is a special case of collections - not its own nav tab
  Details = "details",
  Deposit = "deposit",
  Reports = "reports",
  Administration = "administration",
}

import "./tos-agreement";
import { revealOnFocus } from "../../styles/pageContents";

@customElement("vault-page-container")
export class VaultPageContainer extends LitElement {
  @query("main") protected mainElement: HTMLElement | undefined;

  @property({ type: Boolean }) isAuthenticated: boolean = false;

  // will force the user to agree to terms of service before doing
  // any action on the site except logging out
  @property({ type: Boolean }) shouldShowTos: boolean | undefined = false;
  @property({ type: Boolean }) hasReadPermissions: boolean = false;
  @property({ type: String }) vaultVersion: string = "";
  @property({ type: String }) vaultGitCommitHash: string = "";

  @property({ type: Boolean }) sentryEnabled: boolean = false;
  @property({ type: String }) sentryEnvironment: string = "";
  @property({ type: String }) sentryDsn: string = "";
  @property({ type: String }) sentryTunnelUrl: string = "";
  @property({ type: Number }) sentryTracesSampleRate: number = 0.1;

  @property({ type: String }) orgName: string | undefined = "";
  @property({ type: String }) username: string | undefined = "";
  @property({ type: Number }) userId: number | undefined = undefined;
  @property({ type: String }) userEmail: string | undefined = undefined;

  protected override updated() {
    // initialize sentry once on page load - must use updated hook
    // to access this data
    if (this.sentryEnabled && !VaultSentry.isInitialized) {
      VaultSentry.initialize({
        vaultVersion: this.vaultVersion,
        vaultGitCommitHash: this.vaultGitCommitHash,
        sentryEnvironment: this.sentryEnvironment,
        sentryDsn: this.sentryDsn,
        sentryTunnelUrl: this.sentryTunnelUrl,
        sentryTracesSampleRate: this.sentryTracesSampleRate,
      });
      if (this.isAuthenticated) {
        VaultSentry.client.setUser({
          email: this.userEmail,
          username: this.username,
          id: this.userId,
          orgName: this.orgName,
        });
      }
    }
  }

  protected readonly vaultLogo: TemplateResult = html`<img
    src=${vaultLogo}
    alt="Vault logo"
    class="vault-logo"
  />`;

  protected highlightIfCurrentPage(urlPattern: TopNavUrlPatterns): string {
    const topLevelPathname = window.location.pathname.split("/")[1];
    if (topLevelPathname === TopNavUrlPatterns.Details.toString()) {
      // special case for details routes - they are under collections
      return urlPattern === TopNavUrlPatterns.Collections ? "current-page" : "";
    } else {
      return topLevelPathname === (urlPattern as string) ? "current-page" : "";
    }
  }

  protected focusMainElement() {
    this.mainElement?.focus();
  }

  render() {
    return html`
      <div class="page-container">
        <a
          id="skip-navigation"
          class="reveal-on-focus"
          href="#page-content"
          @click=${this.focusMainElement}
          >Skip to main content</a
        >
        <header>
          <nav>
            <ul>
              <li class="vault-logo-container">
                ${this.hasReadPermissions
                  ? html`
                      <a
                        href="/${desktopState.isDesktop
                          ? TopNavUrlPatterns.Deposit
                          : TopNavUrlPatterns.Dashboard}"
                      >
                        ${this.vaultLogo}
                      </a>
                    `
                  : html`<span>${this.vaultLogo}</span>`}
              </li>
              ${this.hasReadPermissions && !desktopState.isDesktop
                ? html` <li
                    class="header-link ${this.highlightIfCurrentPage(
                      TopNavUrlPatterns.Dashboard,
                    )}"
                  >
                    <a href="/${TopNavUrlPatterns.Dashboard}">
                      <span class="header-link-text"> Dashboard </span>
                    </a>
                  </li>`
                : null}
              <li
                class="header-link ${this.highlightIfCurrentPage(
                  TopNavUrlPatterns.Collections,
                )}"
              >
                <a href="/${TopNavUrlPatterns.Collections}">
                  <span class="header-link-text"> Collections </span>
                </a>
              </li>
              ${this.hasReadPermissions
                ? html` <li
                    class="header-link ${this.highlightIfCurrentPage(
                      TopNavUrlPatterns.Deposit,
                    )}"
                  >
                    <a href="/${TopNavUrlPatterns.Deposit}">
                      <span class="header-link-text"> Deposit </span>
                    </a>
                  </li>`
                : null}
              ${this.hasReadPermissions && !desktopState.isDesktop
                ? html`
                    <li
                      class="header-link ${this.highlightIfCurrentPage(
                        TopNavUrlPatterns.Reports,
                      )}"
                    >
                      <a href="/${TopNavUrlPatterns.Reports}">
                        <span class="header-link-text"> Reports </span>
                      </a>
                    </li>
                    <li
                      class="header-link ${this.highlightIfCurrentPage(
                        TopNavUrlPatterns.Administration,
                      )}"
                    >
                      <a href="/${TopNavUrlPatterns.Administration}">
                        <span class="header-link-text"> Administration </span>
                      </a>
                    </li>
                  `
                : null}
              ${this.isAuthenticated && this.orgName && this.hasReadPermissions
                ? html`
                    <li class="push-right top-right-nav">
                      <a href="/${TopNavUrlPatterns.Dashboard}">
                        ${this.orgName}
                      </a>
                    </li>
                  `
                : null}
              ${this.isAuthenticated && this.orgName && !this.hasReadPermissions
                ? html`<li class="push-right top-right-nav">
                    <div>${this.orgName}</div>
                  </li>`
                : null}
              <li
                class=${`${!this.isAuthenticated ? "push-right" : ""} top-right-nav`}
              >
                <a
                  target="_blank"
                  href="https://vault-webservices.zendesk.com/hc/en-us"
                  >Help Center</a
                >
              </li>
              ${!this.isAuthenticated
                ? html`<li class="top-right-nav">
                    <a href="/accounts/login">Log In</a>
                  </li>`
                : html``}
              ${this.isAuthenticated && this.username
                ? html`
                    <li class="top-right-nav nav-hover-menu">
                      <span class="menu-text">Welcome, ${this.username}</span>
                      <span class="menu-arrow">▾</span>
                      <menu class="reveal-on-focus">
                        <li>
                          <a href="/accounts/password_change"
                            >Change Password</a
                          >
                        </li>
                        <li>
                          <form
                            action="/accounts/logout_then_login"
                            method="post"
                          >
                            <button class="logout">Logout</button>
                            <input
                              id="csrf-token-input"
                              type="hidden"
                              name="csrfmiddlewaretoken"
                              value=${getCsrfHeader()["X-CSRFToken"]}
                            />
                          </form>
                        </li>
                      </menu>
                    </li>
                  `
                : null}
            </ul>
          </nav>
        </header>
        <main id="page-content" tabindex="-1">
          ${this.shouldShowTos && this.userId !== undefined
            ? html`<tos-agreement .userId=${this.userId}></tos-agreement>`
            : html`<slot></slot>`}
        </main>
      </div>
    `;
  }

  static styles = [
    revealOnFocus,
    css`
      :host {
        --border-marker-size: 6px;
      }

      div.page-container {
        height: 100vh;
        width: 100vw;
        display: flex;
        flex-direction: column;
      }

      a#skip-navigation {
        padding: 4px 8px;
        color: var(--vault-light-blue);
        font-weight: bold;
        background: white;
        text-decoration: underline;
      }

      nav {
        width: 100%;
        height: var(--vault-site-nav-height);
        background: var(--vault-top-bar-black);
        display: flex;
        color: white;
      }

      ul {
        width: 100%;
        display: flex;
      }

      ul,
      menu {
        margin: 0;
        padding: 0;
        list-style-type: none;
      }

      menu {
        background: var(--vault-top-bar-black);
        right: 0;
      }

      menu.reveal-on-focus:focus-within,
      li.top-right-nav:hover menu {
        z-index: 1;
        top: 26px;
      }

      li {
        border-right: 1px solid #3a3a3a;
      }

      a {
        display: block;
        text-decoration: none;
        color: white;
      }

      li.header-link > a {
        border-bottom: var(--border-marker-size) solid
          var(--vault-top-bar-black);
        box-sizing: border-box;
      }

      span.header-link-text {
        display: flex;
        position: relative;
        top: calc(var(--border-marker-size) / 2);
      }

      li.current-page > a {
        border-bottom: var(--border-marker-size) solid var(--vault-light-blue);
      }

      li.header-link > a:hover {
        border-bottom: var(--border-marker-size) solid var(--vault-light-blue);
      }

      li.vault-logo-container > a,
      li.vault-logo-container > span,
      li.header-link > a,
      li.header-link > span {
        height: var(--vault-site-nav-height);
        padding: 0 22px;
        display: flex;
        align-items: center;
        font-size: 14.5px;
      }

      li.vault-logo-container {
        background-color: var(--vault-light-blue);
        margin-left: 25px;
      }

      img.vault-logo {
        height: 58px;
        padding: 4px 0 8px 0;
      }

      li.top-right-nav {
        display: flex;
        height: 28px;
        font-size: 12px;
      }

      li.push-right {
        /* placing this on a single item moves it and remaining li items to the end */
        margin-left: auto;
      }

      li > a,
      li > button,
      li > div {
        cursor: pointer;
      }

      li.top-right-nav button {
        flex: 1;
      }

      button.logout {
        all: unset;
      }

      button:focus {
        outline: revert;
      }

      li.top-right-nav a,
      li.top-right-nav div,
      .menu-arrow,
      button.logout {
        padding: 8px 12px;
        cursor: pointer;
      }

      span.menu-text {
        cursor: pointer;
        padding: 8px 0px 8px 12px;
      }

      span.menu-text {
        display: inline-block;
      }

      menu > li {
        border-bottom: 1px solid #3a3a3a;
      }

      menu > li:hover {
        background-color: var(--vault-button-hover-background-color);
      }

      menu > li > form {
        display: flex;
      }

      main {
        width: 100%;
        flex-grow: 1;
        overflow: auto;
      }

      nav {
        position: relative;
      }
    `,
  ];
}
